import { Plus, RotateCcw, Smartphone } from 'lucide-react';
import ToggleSwitch from '../BaseComponents/Toggle/ToggleSwitch';
import TravelIconPicker from '../../screens/CMS/Layout/HomepageSettings/components/TravelIconPicker';
import { isEmoji } from '../../screens/CMS/Layout/HomepageSettings/components/travelIcons';
import { useToast } from '../../context/ToastContext';
import {
  MAX_MOBILE_ITEMS, SECTION, SERVICE_ITEMS, moveItem, newMobileItem,
} from './siteHeaderModel';
import { SERVICE_DEFAULTS } from './headerDefaults';
import { CUSTOM_ROW_ICON, SERVICE_ICONS, SERVICE_NAMES } from './headerIcons';
import {
  BilingualInput, BuiltInTile, LinkField, RowActions, SectionCard, StatusChip, SwitchRow,
  useFieldError,
} from './editorControls';
import styles from './HeaderEditor.module.css';

const PATH = 'mobileMenu.items';

function MobileRow({ item, index, count, onPatch, onMove, onRemove }) {
  const base = `${PATH}.${item.id}`;
  const builtIn = Boolean(item.key);
  const defaultUrl = SERVICE_ITEMS.find((s) => s.key === item.key)?.url;
  const labelError = useFieldError(`${base}.label`);
  const urlError = useFieldError(`${base}.url`);
  const hasError = Boolean(labelError || urlError);
  const name = builtIn
    ? SERVICE_NAMES[item.key]
    : item.label?.en || item.label?.nl || 'New row';
  return (
    <div className={`${styles.itemCard} ${item.active ? '' : styles.itemCardOff} ${hasError ? styles.itemCardError : ''}`}>
      <div className={styles.itemTop}>
        <span className={styles.orderIndex}>{index + 1}</span>
        <TravelIconPicker
          kind="emoji"
          label="Row icon"
          value={isEmoji(item.icon) ? item.icon : ''}
          autoTile={(size) => <BuiltInTile icon={builtIn ? SERVICE_ICONS[item.key] : CUSTOM_ROW_ICON} size={size} />}
          autoLabel="Built-in icon"
          autoHint={builtIn ? `The website's ${SERVICE_NAMES[item.key].toLowerCase()} icon` : 'A plain link icon'}
          onChange={(icon) => onPatch({ icon })}
        />
        <div className={styles.itemTitle}>
          <strong>{name}</strong>
          <span>{builtIn ? <StatusChip tone="blue">Built-in</StatusChip> : <StatusChip tone="muted">Custom</StatusChip>}</span>
        </div>
        <span className={styles.toggleWrap} title={item.active ? 'Shown in the phone menu' : 'Hidden from the phone menu'}>
          <ToggleSwitch checked={item.active} onChange={() => onPatch({ active: !item.active })} />
        </span>
        {/* A built-in row is switched off rather than removed. */}
        <RowActions index={index} count={count} onMove={onMove} onRemove={builtIn ? undefined : onRemove} removeTitle="Remove row" />
      </div>
      <div className={styles.grid2}>
        <BilingualInput
          label={builtIn ? 'Label (optional)' : 'Label'}
          value={item.label}
          required={!builtIn}
          defaults={builtIn ? SERVICE_DEFAULTS[item.key] : undefined}
          placeholder="e.g. Cruises"
          errorPath={`${base}.label`}
          onChange={(label) => onPatch({ label })}
          compact
        />
        <LinkField
          label={builtIn ? 'Link (optional)' : 'Link'}
          value={item.url}
          required={!builtIn}
          placeholder={defaultUrl || '/page or https://'}
          hint={builtIn ? `Empty opens ${defaultUrl}.` : undefined}
          errorPath={`${base}.url`}
          newTab={item.newTab}
          onChange={(url) => onPatch({ url })}
          onNewTab={(newTab) => onPatch({ newTab })}
        />
      </div>
    </div>
  );
}

/**
 * The phone menu (the drawer behind the menu button): its own rows first, then optionally the
 * dropdown menus as groups, the search box at the top, then language and account.
 */
export default function MobileMenuSection({ value, set, patch, errorCount }) {
  const { showToast } = useToast();
  const items = value.items || [];
  const full = items.length >= MAX_MOBILE_ITEMS;
  const updateItems = (fn) => set(PATH, (list) => fn(list || []));
  const missing = SERVICE_ITEMS.filter((s) => !items.some((it) => it.key === s.key));

  const addRow = (key = '') => {
    if (full) {
      showToast(`The phone menu holds at most ${MAX_MOBILE_ITEMS} rows`, 'error');
      return;
    }
    updateItems((list) => [...list, newMobileItem(list.map((it) => it.id), key)]);
    showToast(
      key ? `${SERVICE_NAMES[key]} added back to the phone menu` : 'Row added. Give it a label and a link.',
      'success'
    );
  };

  const removeRow = (index) => {
    const it = items[index];
    const name = it?.key ? SERVICE_NAMES[it.key] : it?.label?.en || it?.label?.nl || 'Row';
    updateItems((list) => list.filter((_, i) => i !== index));
    showToast(`${name} removed from the phone menu`, 'success');
  };

  return (
    <SectionCard
      id={SECTION.mobile}
      icon={Smartphone}
      tone="dark"
      title="Mobile menu"
      description="What the menu button opens on phones (900px wide and below)."
      errorCount={errorCount}
      previewTarget="mobile"
    >
      <div className={styles.grid2}>
        <SwitchRow
          title="Search at the top"
          description="Uses the Search block's settings; hidden when Search is off or desktop only."
          checked={value.showSearch}
          onChange={(showSearch) => patch('mobileMenu', { showSearch })}
        />
        <SwitchRow
          title="Dropdown menus as groups"
          description="Lists the menus under the rows below, in the menu order, each opening in place."
          checked={value.showMenus}
          onChange={(showMenus) => patch('mobileMenu', { showMenus })}
        />
      </div>

      <div className={styles.itemList}>
        {items.length === 0 && <p className={styles.emptyNote}>No rows: the phone menu starts with the dropdown menus.</p>}
        {items.map((it, index) => (
          <MobileRow
            key={it.id}
            item={it}
            index={index}
            count={items.length}
            onPatch={(p) => updateItems((list) => list.map((x) => (x.id === it.id ? { ...x, ...p } : x)))}
            onMove={(i, dir) => updateItems((list) => moveItem(list, i, dir))}
            onRemove={removeRow}
          />
        ))}
        <div className={styles.addRow}>
          <button type="button" className={styles.secondaryBtn} onClick={() => addRow('')} disabled={full}>
            <Plus size={14} /> Add custom row
          </button>
          {missing.map((s) => (
            <button key={s.key} type="button" className={styles.ghostBtn} onClick={() => addRow(s.key)} disabled={full}>
              <RotateCcw size={13} /> Restore {SERVICE_NAMES[s.key]}
            </button>
          ))}
          <span className={styles.hint}>{items.length} / {MAX_MOBILE_ITEMS} rows</span>
        </div>
      </div>
    </SectionCard>
  );
}
